import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  GoogleAuthProvider,
  getRedirectResult,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  signInWithPopup,
  signInWithRedirect,
  signOut as fbSignOut,
} from "firebase/auth";
import { Box, CircularProgress } from "@mui/material";
import { auth } from "../firebaseConfig";

const AuthContext = createContext(null);


export function AuthProvider({ children, notify }) {
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);

  // NEW: block app render until redirect has been processed
  const [bootReady, setBootReady] = useState(false);

  const initOnceRef = useRef(false);
  const redirectHadUserRef = useRef(false);
  const redirectErrorRef = useRef(null);
  const redirectPendingRef = useRef(false);
  const warnTimerRef = useRef(null);

  useEffect(() => {
    if (initOnceRef.current) return;
    initOnceRef.current = true;

    let unsub = null;

    (async () => {
      await setPersistence(auth, browserLocalPersistence);

      // capture pending flag BEFORE redirect check
      const pending = sessionStorage.getItem("mg_google_redirect_pending") === "1";
      redirectPendingRef.current = pending;

      try {
        const result = await getRedirectResult(auth);
        redirectHadUserRef.current = !!result?.user;
      } catch (e) {
        redirectErrorRef.current = e;
      } finally {
        // Always clear after the check. This avoids repeated warnings.
        if (pending) sessionStorage.removeItem("mg_google_redirect_pending");

        // Now it is safe to mount the rest of the app (router/analytics/etc)
        setBootReady(true);
      }

      unsub = onAuthStateChanged(auth, (u) => {
        if (u) {
          redirectHadUserRef.current = true;
        }
        setUser(u);
        setAuthReady(true);
      });
    })();

    return () => {
      unsub?.();
      if (warnTimerRef.current) {
        clearTimeout(warnTimerRef.current);
        warnTimerRef.current = null;
      }
    };
  }, []);

  // Show "did not complete" ONLY after auth settles and ONLY if redirect was pending
  useEffect(() => {
    if (!bootReady) return;
    if (!authReady) return;

    const pending = redirectPendingRef.current;
    const redirectHadUser = redirectHadUserRef.current;
    const redirectErr = redirectErrorRef.current;

    if (!pending) return;

    if (user || auth.currentUser) return; // signed in anyway, no warning needed

    if (redirectErr) {
      notify?.(`Google sign-in failed: ${redirectErr?.code || "unknown-error"}`, "error");
      return;
    }

    if (!redirectHadUser) {
      if (warnTimerRef.current) {
        clearTimeout(warnTimerRef.current);
      }
      // Give auth a moment to settle before warning.
      warnTimerRef.current = setTimeout(() => {
        if (auth.currentUser) return;
        notify?.("Google sign-in did not complete. Please try again.", "warning");
      }, 1500);
    }
  }, [bootReady, authReady, user, notify]);

  const googleSignIn = async () => {
    await setPersistence(auth, browserLocalPersistence);
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });
    try {
      const ua = navigator.userAgent || "";
      const isIOS = /iPhone|iPad|iPod/i.test(ua);
      if (isIOS) {
        sessionStorage.setItem("mg_google_redirect_pending", "1");
        await signInWithRedirect(auth, provider);
        return;
      }

      return await signInWithPopup(auth, provider);
    } catch (e) {
      const code = e?.code || "";
      const popupFail =
        code === "auth/popup-blocked" ||
        code === "auth/popup-closed-by-user" ||
        code === "auth/cancelled-popup-request";

      if (popupFail) {
        sessionStorage.setItem("mg_google_redirect_pending", "1");
        await signInWithRedirect(auth, provider);
        return;
      }

      throw e;
    }
  };

  const signOut = () => fbSignOut(auth);

  const value = useMemo(
    () => ({ user, authReady, bootReady, googleSignIn, signOut }),
    [user, authReady, bootReady]
  );

  // ✅ Critical: prevent Router/analytics from mounting until bootReady
  if (!bootReady) {
    return (
      <Box sx={{ minHeight: "60vh", display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
